/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — import.ts (orchestrator + barrel)
   Trial balance import: orchestrates parser, wizard, classifier, and
   applies imported data to project state.
   ═══════════════════════════════════════════════════════════════════════ */

import {
  state,
  SECTIONS,
  SECTION_SIGN,
  VALID_SECTIONS,
  type SectionData,
} from './state';
import { esc, fmt, el } from './utils';
import { saveProject } from './data';
import { buildTrialBalanceEditor, loadPriorYearFields } from './ui';
import { saveCurrentToSelectedEntity } from './consolidation';

import {
  type TBRow,
  type TBResult,
  wizardRowsToTB,
} from './import-parser';
import {
  getWizardRawRows,
  getWizardMapping,
} from './import-wizard';
import { autoClassify } from './import-classify';

// ─── Barrel re-exports ────────────────────────────────────────────────────
// Re-export public surface so callers can keep importing from './import'.

export {
  parseCSV,
  parseTB,
  parseAmount,
  findCol,
  normalizeHeader,
  wizardRowsToTB,
} from './import-parser';

export {
  zoneDrag,
  zoneDragEnd,
  zoneDrop,
  fileChosen,
  importWizardBack,
} from './import-wizard';

export { autoClassify } from './import-classify';

// ─── Section groups for unmapped-row dropdowns ────────────────────────────

interface SectionGroupOption {
  value: string;
  text: string;
}

interface SectionGroup {
  label: string;
  options: SectionGroupOption[];
}

/**
 * Import section groups with labels for <optgroup> rendering.
 * Different structure from SECTION_GROUPS in state.ts (which is string[][]).
 */
const SECTION_GROUPS: SectionGroup[] = [
  { label: 'Income Statement', options: [
    { value: 'revenue',    text: 'Revenue' },
    { value: 'cogs',       text: 'Cost of Goods Sold' },
    { value: 'opex',       text: 'Operating Expenses' },
    { value: 'other',      text: 'Other Income / Expense' },
  ]},
  { label: 'Balance Sheet', options: [
    { value: 'current-assets',    text: 'Current Assets' },
    { value: 'noncurrent-assets', text: 'Non-Current Assets' },
    { value: 'current-liab',      text: 'Current Liabilities' },
    { value: 'noncurrent-liab',   text: 'Non-Current Liabilities' },
    { value: 'equity',            text: "Shareholders' Equity" },
  ]},
  { label: 'Cash Flow', options: [
    { value: 'cf-operating',  text: 'Operating Activities' },
    { value: 'cf-investing',  text: 'Investing Activities' },
    { value: 'cf-financing',  text: 'Financing Activities' },
  ]},
];

// ─── Module-level state (orchestrator-owned) ──────────────────────────────

let pendingComparativeUnmapped: TBRow[] = [];

// ─── Import Workflow ──────────────────────────────────────────────────────

export async function runImport(): Promise<void> {
  try {
    const wizardRawRows = getWizardRawRows();
    if (wizardRawRows.length < 2) {
      showStatus('error', 'Please select a file to import.');
      return;
    }

    const mapping = getWizardMapping();
    const tbRows = wizardRowsToTB(wizardRawRows, mapping);

    // ── TB Balance Validation ──
    let totalDebits = 0, totalCredits = 0;
    let pTotalDebits = 0, pTotalCredits = 0;
    const hasPrior = tbRows._hasComparative;
    for (const row of tbRows) {
      totalDebits += row.debit;
      totalCredits += row.credit;
      if (hasPrior && row.priorDebit !== undefined && row.priorCredit !== undefined) {
        pTotalDebits += row.priorDebit;
        pTotalCredits += row.priorCredit;
      }
    }
    const curDiff = Math.abs(totalDebits - totalCredits);
    const priorDiff = hasPrior ? Math.abs(pTotalDebits - pTotalCredits) : 0;
    if (curDiff > 0.01 || priorDiff > 0.01) {
      let warnMsg = '<div class="import-status warning" style="margin-bottom:12px"><strong>&#9888; Trial Balance Warning:</strong> ';
      if (curDiff > 0.01) {
        warnMsg += 'Current period debits (' + fmt(totalDebits) + ') ≠ credits (' + fmt(totalCredits) + '), difference of ' + fmt(curDiff) + '. ';
      }
      if (priorDiff > 0.01) {
        warnMsg += 'Prior period debits (' + fmt(pTotalDebits) + ') ≠ credits (' + fmt(pTotalCredits) + '), difference of ' + fmt(priorDiff) + '. ';
      }
      warnMsg += 'Your balance sheet may not balance. Check your trial balance for errors.</div>';
      const statusEl = el('import-status');
      if (statusEl) statusEl.innerHTML = warnMsg;
    }

    if (tbRows._hasComparative) {
      // File has prior year columns — import both periods
      SECTIONS.forEach(s => { state.currentData[s] = []; state.priorData[s] = []; });
      applyComparativeImport(tbRows, null);
    } else {
      // Current period only
      SECTIONS.forEach(s => { state.currentData[s] = []; });
      applyImport(tbRows, null, state.currentData);
    }

    // If in consolidation mode, sync imported data back to the selected entity
    if (state.consolidationMode) {
      saveCurrentToSelectedEntity();
    }
  } catch (err: any) {
    showStatus('error', err.message);
  }
}

function applyImport(
  tbRows: TBResult,
  accountMap: Map<string, string> | null,
  targetData: SectionData
): void {
  const unmapped: TBRow[] = [];
  let imported = 0;

  for (const row of tbRows) {
    let section = row.section !== undefined
      ? row.section
      : (accountMap ? accountMap.get(row.accountNumber) : undefined);
    // Auto-classify if no section from CSV or account map
    if (!section || !VALID_SECTIONS.has(section)) {
      section = autoClassify(row.accountNumber, row.accountName);
    }
    if (!section || !VALID_SECTIONS.has(section)) {
      unmapped.push({ ...row, section: section || '' });
      continue;
    }
    const sign   = SECTION_SIGN[section];
    const amount = sign * (row.debit - row.credit);
    targetData[section].push({ label: row.accountName, amount: parseFloat(amount.toFixed(2)) });
    imported++;
  }

  saveProject();
  buildTrialBalanceEditor();
  loadPriorYearFields();
  state.pendingUnmapped = unmapped;
  state.pendingTargetData = targetData;

  let html = '';

  if (imported > 0) {
    html += '<div class="import-status success" style="margin-bottom:' + (unmapped.length ? '12px' : '0') + '">';
    html += '<strong>' + imported + ' account' + (imported !== 1 ? 's' : '') + ' auto-classified and imported.</strong>';
    html += '</div>';
  }

  if (unmapped.length) {
    html += '<div class="import-status info">';
    html += '<strong>' + unmapped.length + ' account' + (unmapped.length !== 1 ? 's' : '') + ' need' + (unmapped.length === 1 ? 's' : '') + ' a section assignment.</strong>';
    html += ' Use the dropdowns to assign each account to the correct financial statement section.';
    html += '<div class="unmapped-list"><table>';
    html += '<tr><th>Account #</th><th>Name</th><th>Debit</th><th>Credit</th><th style="min-width:180px">Assign Section</th></tr>';
    for (let i = 0; i < unmapped.length; i++) {
      const u = unmapped[i];
      html += '<tr>' +
        '<td>' + esc(u.accountNumber) + '</td>' +
        '<td>' + esc(u.accountName) + '</td>' +
        '<td>' + fmt(u.debit) + '</td>' +
        '<td>' + fmt(u.credit) + '</td>' +
        '<td>' + buildSectionSelect('map-' + i, '') + '</td>' +
      '</tr>';
    }
    html += '</table></div>';
    html += '<div class="map-actions">' +
      '<button class="btn btn-primary btn-sm" data-action="applyMappings">Map & Import</button>' +
      '<span class="map-count" id="map-count">0 of ' + unmapped.length + ' assigned</span>' +
    '</div>';
    html += '</div>';
  }

  if (!imported && !unmapped.length) {
    html = '<div class="import-status error">No accounts found in file.</div>';
  }

  const importStatus = el('import-status');
  if (importStatus) importStatus.innerHTML = html;
}

// ─── Comparative Import (both periods from one file) ──────────────────────

function applyComparativeImport(
  tbRows: TBResult,
  accountMap: Map<string, string> | null
): void {
  const unmapped: TBRow[] = [];
  let importedCur = 0;
  let importedPrior = 0;

  for (const row of tbRows) {
    let section = row.section !== undefined
      ? row.section
      : (accountMap ? accountMap.get(row.accountNumber) : undefined);
    // Auto-classify if no section from CSV or account map
    if (!section || !VALID_SECTIONS.has(section)) {
      section = autoClassify(row.accountNumber, row.accountName);
    }
    if (!section || !VALID_SECTIONS.has(section)) {
      unmapped.push({ ...row, section: section || '' });
      continue;
    }
    const sign = SECTION_SIGN[section];

    // Current period
    const curAmount = sign * (row.debit - row.credit);
    if (Math.abs(curAmount) > 0.005 || (row.debit === 0 && row.credit === 0)) {
      state.currentData[section].push({ label: row.accountName, amount: parseFloat(curAmount.toFixed(2)) });
      importedCur++;
    }

    // Prior period
    if (row.priorDebit !== undefined && row.priorCredit !== undefined) {
      const priorAmount = sign * (row.priorDebit - row.priorCredit);
      if (Math.abs(priorAmount) > 0.005 || (row.priorDebit === 0 && row.priorCredit === 0)) {
        state.priorData[section].push({ label: row.accountName, amount: parseFloat(priorAmount.toFixed(2)) });
        importedPrior++;
      }
    }
  }

  saveProject();
  pendingComparativeUnmapped = unmapped;

  let html = '';

  if (importedCur > 0 || importedPrior > 0) {
    html += '<div class="import-status success" style="margin-bottom:' + (unmapped.length ? '12px' : '0') + '">';
    html += '<strong>Comparative import: ' + importedCur + ' current + ' + importedPrior + ' prior period accounts imported.</strong>';
    html += '</div>';
  }

  if (unmapped.length) {
    html += '<div class="import-status info">';
    html += '<strong>' + unmapped.length + ' account' + (unmapped.length !== 1 ? 's' : '') + ' need' + (unmapped.length === 1 ? 's' : '') + ' a section assignment.</strong>';
    html += ' Use the dropdowns to assign each account to the correct financial statement section.';
    html += '<div class="unmapped-list"><table>';
    html += '<tr><th>Account #</th><th>Name</th><th>Debit</th><th>Credit</th><th>Prior Dr</th><th>Prior Cr</th><th style="min-width:180px">Assign Section</th></tr>';
    for (let i = 0; i < unmapped.length; i++) {
      const u = unmapped[i];
      html += '<tr>' +
        '<td>' + esc(u.accountNumber) + '</td>' +
        '<td>' + esc(u.accountName) + '</td>' +
        '<td>' + fmt(u.debit) + '</td>' +
        '<td>' + fmt(u.credit) + '</td>' +
        '<td>' + fmt(u.priorDebit || 0) + '</td>' +
        '<td>' + fmt(u.priorCredit || 0) + '</td>' +
        '<td>' + buildSectionSelect('map-' + i, '') + '</td>' +
      '</tr>';
    }
    html += '</table></div>';
    html += '<div class="map-actions">' +
      '<button class="btn btn-primary btn-sm" data-action="applyComparativeMappings">Map & Import</button>' +
      '<span class="map-count" id="map-count">0 of ' + unmapped.length + ' assigned</span>' +
    '</div>';
    html += '</div>';
  }

  if (!importedCur && !importedPrior && !unmapped.length) {
    html = '<div class="import-status error">No accounts found in file.</div>';
  }

  const importStatus = el('import-status');
  if (importStatus) importStatus.innerHTML = html;
}

export function applyComparativeMappings(): void {
  let added = 0;
  for (let i = 0; i < pendingComparativeUnmapped.length; i++) {
    const sel = document.getElementById('map-' + i) as HTMLSelectElement | null;
    const section = sel ? sel.value : '';
    if (!section || !VALID_SECTIONS.has(section)) continue;
    const row = pendingComparativeUnmapped[i];
    const sign = SECTION_SIGN[section];

    // Current
    const curAmount = sign * (row.debit - row.credit);
    state.currentData[section].push({ label: row.accountName, amount: parseFloat(curAmount.toFixed(2)) });

    // Prior
    if (row.priorDebit !== undefined && row.priorCredit !== undefined) {
      const priorAmount = sign * (row.priorDebit - row.priorCredit);
      state.priorData[section].push({ label: row.accountName, amount: parseFloat(priorAmount.toFixed(2)) });
    }
    added++;
  }
  if (added === 0) { showStatus('error', 'No sections assigned. Use the dropdowns to map accounts first.'); return; }
  pendingComparativeUnmapped = [];
  saveProject();
  buildTrialBalanceEditor();
  loadPriorYearFields();
  const total = SECTIONS.reduce((n: number, s: string) => n + state.currentData[s].length + state.priorData[s].length, 0);
  showStatus('success', '<strong>' + added + ' account' + (added !== 1 ? 's' : '') + ' mapped (both periods).</strong>' + ' ' + total + ' total accounts loaded.', true);
}

function buildSectionSelect(id: string, preselect: string): string {
  let html = '<select id="' + id + '" data-mapping-change="true">';
  html += '<option value="">-- Select section --</option>';
  for (const group of SECTION_GROUPS) {
    html += '<optgroup label="' + group.label + '">';
    for (const opt of group.options) {
      const sel = opt.value === preselect ? ' selected' : '';
      html += '<option value="' + opt.value + '"' + sel + '>' + opt.text + '</option>';
    }
    html += '</optgroup>';
  }
  html += '</select>';
  return html;
}

export function onMappingChange(sel: HTMLSelectElement): void {
  sel.classList.toggle('mapped', sel.value !== '');
  updateMapCount();
}

function updateMapCount(): void {
  const countEl = el('map-count');
  if (!countEl) return;
  const total = state.pendingUnmapped.length;
  const mapped = state.pendingUnmapped.filter((_: any, i: number) => {
    const s = document.getElementById('map-' + i) as HTMLSelectElement | null;
    return s && s.value;
  }).length;
  countEl.textContent = mapped + ' of ' + total + ' assigned';
}

export function applyMappings(): void {
  const targetData: SectionData = state.pendingTargetData || state.currentData;
  let added = 0;
  for (let i = 0; i < state.pendingUnmapped.length; i++) {
    const sel = document.getElementById('map-' + i) as HTMLSelectElement | null;
    const section = sel ? sel.value : '';
    if (!section || !VALID_SECTIONS.has(section)) continue;
    const row = state.pendingUnmapped[i];
    const sign = SECTION_SIGN[section];
    const amount = sign * (row.debit - row.credit);
    targetData[section].push({ label: row.accountName, amount: parseFloat(amount.toFixed(2)) });
    added++;
  }
  if (added === 0) { showStatus('error', 'No sections assigned. Use the dropdowns to map accounts first.'); return; }
  state.pendingUnmapped = [];
  state.pendingTargetData = null;
  saveProject();
  buildTrialBalanceEditor();
  const total = SECTIONS.reduce((n: number, s: string) => n + state.currentData[s].length + state.priorData[s].length, 0);
  showStatus('success', '<strong>' + added + ' account' + (added !== 1 ? 's' : '') + ' mapped and imported.</strong> ' + total + ' total accounts loaded.', true);
}

function showStatus(type: string, msg: string, trusted?: boolean): void {
  const importStatus = el('import-status');
  if (importStatus) importStatus.innerHTML =
    '<div class="import-status ' + type + '">' + (trusted ? msg : esc(msg)) + '</div>';
}

// ─── Template Download ────────────────────────────────────────────────────

export function downloadTemplate(): void {
  const csv: string = 'Account Number,Account Name,Debit,Credit,Prior Debit,Prior Credit,Section\n' +
    '1000,Cash,124000,0,98000,0,current-assets\n' +
    '1100,Accounts Receivable,88000,0,72000,0,current-assets\n' +
    '1200,Inventory,210000,0,195000,0,current-assets\n' +
    '1500,Equipment,350000,0,320000,0,noncurrent-assets\n' +
    '1510,Accumulated Depreciation,0,85000,0,60000,noncurrent-assets\n' +
    '2000,Accounts Payable,0,64000,0,52000,current-liab\n' +
    '2500,Long-Term Debt,0,245000,0,280000,noncurrent-liab\n' +
    '3000,Common Stock,0,100000,0,100000,equity\n' +
    '3100,Retained Earnings,0,120000,0,75000,equity\n' +
    '4000,Product Sales,0,850000,0,720000,revenue\n' +
    '5000,Cost of Goods Sold,310000,0,265000,0,cogs\n' +
    '6000,Salaries & Wages,148000,0,132000,0,opex\n' +
    '6100,Depreciation Expense,25000,0,20000,0,opex\n' +
    '6200,Rent Expense,36000,0,36000,0,opex\n' +
    '7100,Interest Expense,14200,0,16500,0,other\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(blob), download: 'import_template.csv'
  });
  a.click(); URL.revokeObjectURL(a.href);
}

// ---------------------------------------------------------------------------
// Event Delegation — mapping select change handlers
// ---------------------------------------------------------------------------

document.addEventListener('change', function (e: Event) {
  const target = e.target as HTMLElement;
  if (target.hasAttribute('data-mapping-change')) {
    onMappingChange(target as HTMLSelectElement);
  }
  if (target.hasAttribute('data-wiz-change')) {
    (target as HTMLSelectElement).classList.toggle('mapped', (target as HTMLSelectElement).value !== '');
  }
});
